<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Eine Virtual Tour zu Ihrem Google Business Profil hinzuzufügen ist
            eine der lokalen SEO-Maßnahmen mit dem höchsten ROI, die Sie 2026
            ergreifen können. Unternehmen mit Virtual Tours verzeichnen
            <strong>2× mehr Profilengagement</strong> und höhere
            Konversionsraten aus Google Maps-Suchen. Hier finden Sie die genaue
            Schritt-für-Schritt-Anleitung.
          </p>
        </section>

        <section>
          <h2>
            Warum eine Virtual Tour zum Google Business Profil hinzufügen?
          </h2>
          <p>
            Googles Algorithmus belohnt vollständige Business Profile mit
            höheren lokalen Such-Rankings. Eine Virtual Tour signalisiert, dass
            Ihr Profil vertrauenswürdig, verifiziert und hochwertig ist. Kunden,
            die eine Virtual Tour ansehen, sind
            <strong>3× häufiger bereit zu besuchen</strong> als solche, die nur
            Fotos sehen.
          </p>
        </section>

        <section>
          <h2>Voraussetzungen</h2>
          <p>
            Sie benötigen: ein verifiziertes Google Business Profil,
            äquirektangulare 360°-Bilder (mindestens 7,5 Megapixel, idealerweise
            14 MP+) und entweder die Google Street View App oder einen
            Travvir-Export. Bilder müssen echte Fotos Ihres tatsächlichen
            Unternehmens sein – keine Renderings oder Stockfotos.
          </p>
        </section>

        <section>
          <h2>
            Schritt-für-Schritt: Eine Virtual Tour zum Google Business Profil
            hinzufügen
          </h2>
          <h3>Schritt 1 – Erstellen Sie Ihre 360°-Tour mit Travvir</h3>
          <p>
            Nehmen Sie Ihren Geschäftsraum mit der Travvir-App auf. Stellen Sie
            sicher, dass Sie das Außengelände, den Eingang und die wichtigsten
            Innenbereiche erfassen. Exportieren Sie die Tour-Bilder als
            äquirektangulare JPGs.
          </p>
          <h3>Schritt 2 – Google Business Profil öffnen</h3>
          <p>
            Gehen Sie zu business.google.com und wählen Sie Ihr Unternehmen.
            Klicken Sie im linken Menü auf „Fotos" und wählen Sie dann
            „360°-Fotos" aus dem Untermenü.
          </p>
          <h3>Schritt 3 – Ihre 360°-Bilder hochladen</h3>
          <p>
            Klicken Sie auf „360°-Fotos hochladen." Wählen Sie Ihre
            äquirektangularen Bilder aus. Google erkennt automatisch die
            360°-Metadaten (XMP-Daten), die von Travvir eingebettet werden, und
            zeigt sie korrekt im Viewer an.
          </p>
          <h3>Schritt 4 – Ihr Cover-Tour festlegen</h3>
          <p>
            Im Fotos-Bereich Ihres Business Profils können Sie ein 360°-Bild als
            Ihr „Interior"-Titelbild festlegen. Dieses wird prominent in
            Suchergebnissen und Maps-Einträgen angezeigt.
          </p>
          <h3>Schritt 5 – Über Street View App verbinden (optional)</h3>
          <p>
            Für eine vollständig verknüpfte Mehrraum-Tour (im Gegensatz zu
            einzelnen 360°-Fotos) verwenden Sie die Google Street View App, um
            Ihre hochgeladenen Bilder zu einer navigierbaren Tour zu
            „verbinden". Alternativ können Sie Ihren Travvir-Tour-Link in einem
            Beitrag auf Ihrem Business Profil einbetten.
          </p>
        </section>

        <section>
          <h2>Lokale SEO-Vorteile</h2>
          <p>
            Nach dem Hinzufügen Ihrer Virtual Tour: Beobachten Sie Ihre Business
            Profile Insights auf Anstiege bei „Aufrufe in der Suche" und
            „Aufrufe in Maps". Unternehmen verzeichnen typischerweise einen
            Anstieg von 20–30 % bei Profilaktionen (Anrufe,
            Routenbeschreibungen, Website-Klicks) innerhalb von 60 Tagen nach
            dem Hinzufügen einer Virtual Tour.
          </p>
        </section>

        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li>
              <a href="/blog/de/virtual-tour-seo-leitfaden"
                >Virtual Tour SEO Leitfaden</a
              >
            </li>
            <li>
              <a href="/blog/de/360-fotos-lokales-seo"
                >360 Fotos und lokales SEO</a
              >
            </li>
            <li>
              <a href="/blog/de/google-street-view-virtual-tour"
                >Google Street View Virtual Tour</a
              >
            </li>
            <li>
              <a href="/blog/de/virtual-tour-kleinunternehmen"
                >Virtual Tour für Kleinunternehmen</a
              >
            </li>
            <li>
              <a href="/blog/de/virtual-tour-analysen"
                >Tour-Engagement verfolgen</a
              >
            </li>
            <li>
              <a href="/blog/de/virtual-tour-mit-smartphone-erstellen"
                >Tour mit Smartphone erstellen</a
              >
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "virtual-tour-google-business-profil",
);

useBlogSeo({
  title: "Virtual Tour Google Business Profil [2026] | Travvir",
  description:
    "360°-Virtual-Tour zu Ihrem Google-Business-Profil hinzufügen. Schritt-für-Schritt-Anleitung für bessere lokale Sichtbarkeit.",
  slug: "virtual-tour-google-business-profil",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
